import React, { ChangeEvent, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import {
  ACCEPTABLE_IMAGE_FORMATS_LIST,
  MAX_IMAGE_UPLOAD_SIZE,
} from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { createImage, fileToUrl, ImageCropperModal } from '../../molecules/Modals/image-cropper';
import { CircleIconButton } from '../../Button/circle-icon-button.component';

import { ImageInput } from './image-input.components';
import { AvatarActionsMenu, AvatarActionsMenuItem } from './avatar-actions-menu.component';


const useStyles = makeStyles()({
  cameraInput: {
    position: 'absolute',
    bottom: 15,
    right: 15,
  },
  cameraButton: {
    backgroundColor: '#57807D',
    color: '#fff',
  },

  emptyImageLabel: {
    cursor: 'pointer',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    left: 0,
    top: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)'
  },
});


export type ImageAsset = {
  name?: string;
  original: string;
  cropped: string;
};

type OverrideActionsMenuArgs = {
  onEditCurrentPhoto: (initialImage: ImageAsset) => void;
  initialImage?: ImageAsset;
  htmlFor: string;
};

export type AvatarInputProps = {
  initialImage?: ImageAsset;
  onImageChange: (asset: ImageAsset) => void;
  setError: (error?: string) => void;
  overrideActionsMenu?: (args: OverrideActionsMenuArgs) => React.ReactNode;
};


export const AvatarInput: React.FC<AvatarInputProps> = ({ initialImage, onImageChange, setError, overrideActionsMenu }) => {
  const { classes } = useStyles();
  const [ imageName, setImageName ] = React.useState<string | undefined>(initialImage?.name);
  const [ image, setImage ] = React.useState<string | undefined>(initialImage?.original);
  const [ croppedImage, setCroppedImage ] = React.useState<string | undefined>(initialImage?.cropped);

  const l10n = useL10n();
  const [ cropModalOpen, setCropModalOpen ] = React.useState(false);

  const handleImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];

    if (!file) return;

    if (file.size > MAX_IMAGE_UPLOAD_SIZE) {
      setError(l10n.validations.imageTooBig);
      return;
    }

    const fileUrl = fileToUrl(file);
    const img = await createImage(fileUrl);

    if (img.width < 250) {
      setError(l10n.validations.imageWidthTooBig);
      return;
    }

    if (!ACCEPTABLE_IMAGE_FORMATS_LIST.includes(file.type)) {
      setError(l10n.validations.invalidFileType);
      return;
    }

    setImageName(file.name);
    setImage(fileToUrl(file));
    setError(undefined);
    setCropModalOpen(true);

    event.target.value = '';
  };

  React.useEffect(() => {
    if (croppedImage && image) {
      onImageChange({
        name: imageName,
        original: image,
        cropped: croppedImage,
      });
    }
  }, [ croppedImage ]);


  const onEditCurrentPhoto = (initialImage: ImageAsset) => {
    setImage(initialImage.original);
    setCroppedImage(initialImage.cropped);
    setCropModalOpen(true);
  };

  const actionsMenu = useMemo(() => {
    if (overrideActionsMenu) {
      return overrideActionsMenu({ onEditCurrentPhoto, initialImage, htmlFor: 'photo' });
    }

    if (initialImage) {
      return (
        <AvatarActionsMenu
          button={(
            <span className={classes.cameraInput}>
              <CircleIconButton className={classes.cameraButton}>
                <CameraAltOutlinedIcon color="inherit" />
              </CircleIconButton>
            </span>
          )}
        >
          <AvatarActionsMenuItem onClick={() => onEditCurrentPhoto(initialImage)}>
            Edit current photo
          </AvatarActionsMenuItem>
          <AvatarActionsMenuItem component="label" htmlFor="photo">
            {l10n.matching.profilePhoto.chooseNewPhoto}
          </AvatarActionsMenuItem>
        </AvatarActionsMenu>
      );
    }

    return (
      <label htmlFor="photo" className={classes.emptyImageLabel}>
        <Box textAlign="center">
          <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
            <CloudUploadOutlinedIcon color="primary" fontSize="small" />
            <Box ml={1}>
              <Typography display="block" variant="button" color="primary">{l10n.matching.profilePhoto.choosePhoto}</Typography>
            </Box>
          </Box>
          <Typography display="block" variant="body2" color="textSecondary">{l10n.matching.profilePhoto.photoDetailsFormat},</Typography>
          <Typography display="block" variant="body2" color="textSecondary">{l10n.matching.profilePhoto.photoDetailsSize}</Typography>
        </Box>
      </label>
    );
  }, [ classes, initialImage, l10n.matching.profilePhoto, overrideActionsMenu ]);

  return (
    <>
      <ImageInput id="photo" onChange={handleImage} />
      {actionsMenu}
      {image && (
        <ImageCropperModal
          open={cropModalOpen}
          title="Update profile photo"
          imageUrl={image}
          onClose={() => setCropModalOpen(false)}
          onFileCrop={setCroppedImage}
        />
      )}
    </>
  );
};
